/**
 * Items With Unavailable Quantities - the sales order stage change gate (legacy OMD0016).
 *
 * Shown from Form.js handleStageChange when the server side check
 * (om/SalesOrderHeaderOperations/CheckItemsWithUnavailableQuantities) reports detail lines whose
 * ordered/shipped quantity exceeds what is available. Read only: the only decisions are Continue
 * (allowed only when the transaction profile's STATUSCHECK_xxxx flag is 'A' or 'C') and Cancel.
 *
 * The grid is paged on the server in both toggle states - it never pulls the whole order. The
 * Show All toggle resets to insufficient-only every time the popup opens; the Delphi dialog was
 * cached per sales order session and leaked that state between stage changes.
 *
 * Legacy mnuOptions (navTop Options button) carries two entries and neither has an OnClick in
 * uOMD0016.dfm - both are caption-only stubs that do nothing when clicked. "Open Transactions for
 * Item/Lot" has no other trigger either, so it is not migrated.
 *
 * The Insufficient Quantity Report (frmOMR0038, SR-026582) is reachable in Delphi, just not from that
 * menu item: roOMInsufficientQuantity is a TAbReportObject with ShowOnMenu = True, and uAbNav.pas
 * builds navTop Print button popup out of exactly those, so printing from there calls PrintReport and
 * fires roOMInsufficientQuantityBeforePrintReport. That handler scopes the report to this dialog’s own
 * header and aborts when the header is blank. The Print button below is that path, not the dead menu
 * entry - and it keeps the legacy shape, a button whose dropdown lists the reports this screen offers,
 * which here is the single already migrated OMR0041. It stays disabled without a header.
 *
 * That button is the shared PrintReportMenu, given this dialog's one report instead of the whole OM
 * report menu. Delphi scoped the report by OMTransactionHeader.ROWID; OMR0041 exposes the same header
 * row in its filter list as "Record #" (KEYSEQUENCE), which is the filter PrintReportMenu already
 * passes to print one order.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Button } from "devextreme-react/button";
import { Popup } from "devextreme-react/popup";
import {
    Column,
    DataGrid,
    Export,
    Toolbar,
    Item,
    Pager,
    Paging,
    Scrolling,
    Selection,
    StateStoring,
    ColumnChooser,
    ColumnChooserSearch,
} from "devextreme-react/data-grid";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { alert } from "devextreme/ui/dialog";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import {
    buildImItemStatusFormFields,
    getImItemStatusLayout,
} from "./ImItemStatusLayout";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import "./itemsWithUnavailableQuantities.css";
import PrintReportMenu from "../../salesOrderManagement/common/PrintReportMenu";
import GridLayoutManager from "../../../../../gridLayoutCustomization/GridLayoutManager";
import { GridLayout } from "../../../../../common/GridLayout";

/* Grid captions. Local to this dialog on purpose: the repo writes DataGrid captions at the
   column, and lifting three dozen generic names ('Item', 'Quantity', 'Stage') into the global
   MESSAGES namespace invites collisions with unrelated screens. Server-supplied text still
   comes from Messages.resx via the DTOs. */
const COLUMN = {
    RECORD_NUMBER: "Record #",
    WAREHOUSE_SHORT: "WH",
    ITEM: "Item",
    ITEM_DESCRIPTION_1: "Item Description - 1",
    LOT: "Lot",
    ORDERED: "Ordered",
    SHIPPED: "Shipped",
    BACKORDER: "BackOrder",
    ITEM_AVAILABLE: "Item Available",
    LOT_AVAILABLE: "Lot Available",
    LOCATION: "Location",
    QUANTITY: "Quantity",
    AVAILABLE: "Available",
    VALIDATE_QUANTITY: "Validate Quantity",
    DETAIL_TYPE: "Detail Type",
    REFERENCE_NUMBER: "Reference Number",
    STAGE: "Stage",
    ITEM_TYPE: "Item Type",
    ITEM_DESCRIPTION_2: "Item Description - 2",
    LOT_DESCRIPTION_1: "Lot Description - 1",
    LOT_DESCRIPTION_2: "Lot Description - 2",
    USER_DEFINED_CODE_1: "User Defined Code - 1",
    USER_DEFINED_DATE_1: "User Defined Date - 1",
    USER_DEFINED_QUANTITY_1: "User Defined Quantity - 1",
    USER_DEFINED_REFERENCE_1: "User Defined Reference - 1",
    USER_DEFINED_CODE_2: "User Defined Code - 2",
    USER_DEFINED_DATE_2: "User Defined Date - 2",
    USER_DEFINED_QUANTITY_2: "User Defined Quantity - 2",
    USER_DEFINED_REFERENCE_2: "User Defined Reference - 2",
    USER_DEFINED_CODE_3: "User Defined Code - 3",
    USER_DEFINED_DATE_3: "User Defined Date - 3",
    USER_DEFINED_QUANTITY_3: "User Defined Quantity - 3",
    USER_DEFINED_REFERENCE_3: "User Defined Reference - 3",
    USER_DEFINED_CODE_4: "User Defined Code - 4",
    USER_DEFINED_DATE_4: "User Defined Date - 4",
    USER_DEFINED_QUANTITY_4: "User Defined Quantity - 4",
    USER_DEFINED_REFERENCE_4: "User Defined Reference - 4",
    USER_DEFINED_CODE_5: "User Defined Code - 5",
    USER_DEFINED_DATE_5: "User Defined Date - 5",
    USER_DEFINED_QUANTITY_5: "User Defined Quantity - 5",
    USER_DEFINED_REFERENCE_5: "User Defined Reference - 5",
    ITEM_ON_HAND: "Item On Hand",
    ITEM_IN_RECEIVING: "Item In-Receiving",
    ITEM_IN_SHIPPING: "Item In-Shipping",
    ITEM_UNAVAILABLE: "Item Unavailable",
    ITEM_AVAILABLE_STATUS: "Item Available (Status)",
    ITEM_PENDING_REC: "Item Pending Rec",
    ITEM_PENDING_SHIP: "Item Pending Ship",
    ITEM_NET_AVAILABLE: "Item Net Available",
    ITEM_FUTURE_REC: "Item Future Rec",
    ITEM_FUTURE_SHIP: "Item Future Ship",
    LOT_ON_HAND: "Lot On Hand",
    LOT_IN_RECEIVING: "Lot In-Receiving",
    LOT_IN_SHIPPING: "Lot In-Shipping",
    LOT_UNAVAILABLE: "Lot Unavailable",
    LOT_AVAILABLE_STATUS: "Lot Available (Status)",
    LOT_PENDING_REC: "Lot Pending Rec",
    LOT_PENDING_SHIP: "Lot Pending Ship",
    LOT_NET_AVAILABLE: "Lot Net Available",
    LOT_FUTURE_REC: "Lot Future Rec",
    LOT_FUTURE_SHIP: "Lot Future Ship",
};

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

/* The one report this dialog offers - Delphi's roOMInsufficientQuantity. `name` is
   Argos.Core/Report/OM/OMR0041.Name and the ReportFactory case label it is matched against;
   `permission` is the OMReports permission (AbecasInsight.Policies Permissions.cs) that gates it, so
   printing from here is no more open than reaching the report from the Reports menu. Named once
   because the report has already been renumbered from OMR0038. */
const INSUFFICIENT_QUANTITY_REPORT = Object.freeze({
    name: "OMR0041",
    permission: "InsufficientQuantityReport",
});

/** Delphi grdItemsCustomDraw: stages A/S/C judge the row on shipped, everything else on ordered. */
const STAGES_EFFECTING_AVAILABLE = ["A", "S", "C"];
/** IMItem.ItemType 'L' means the row is judged against lot availability. */
const ITEM_TYPE_LOT = "L";

const QUANTITY_STATUS_GROUP_ID = 2;

/* Delphi pgcBottom: Primary carries the two location grids, Status the item/lot quantity panels. */
const TAB_PRIMARY = 0;

/* Dynamic-form layout identity for the Status tab. One form, two groups - the item side and the
   lot side - which FormField lays out as the two side by side group boxes OMD0016 shows. */
const STATUS_LAYOUT_ID = "0B7F4C61-2E58-4D93-A6C0-91D4E75B3A28";
const STATUS_LAYOUT_NAME = "UnavailableQuantitiesStatus";

/* Both groups come from the same field definitions, so the lot side is keyed apart from the item
   side; without this the two groups would read and write the same values. */
const LOT_FIELD_SUFFIX = "_LOTSIDE";
const LOT_STATUS_GROUP_ID = 3;

/* OMD0016 shows ten quantities per side. The shared IM Item Status layout also carries the two
   backorder rows, which the legacy dialog never displayed. */
const STATUS_FIELDS_NOT_ON_OMD0016 = [
    "IMITEMSTATUS_IN_BACKORDER",
    "IMITEMSTATUS_OUT_BACKORDER",
];

function toNumber(value) {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

/* The quantity panels are read only, so FormField never has a value change to report. */
function noop() { }

/** Delphi qryOMTransactionDetail*GetText: an empty cell for a null or zero quantity. */
function customizeOrderQuantity(cellInfo) {
    return toNumber(cellInfo.value) === 0 ? "" : cellInfo.valueText;
}

/** Delphi grdItemsCustomDraw (uOMD0016.pas:636). */
function isRowInsufficient(row) {
    if (!row) return false;
    const available = row.ItemType === ITEM_TYPE_LOT ? row.LotAvailable : row.ItemAvailable;
    const quantity = STAGES_EFFECTING_AVAILABLE.includes(row.Stage)
        ? row.QuantityShipped
        : row.QuantityOrdered;
    return toNumber(quantity) > toNumber(available);
}

function ItemsWithUnavailableQuantitiesPopup(props) {
    const { showPopup = {}, setShowPopup, onContinue, onCancel, oidc } = props;
    const visible = showPopup.visible === true;
    const accessToken = oidc?.user?.access_token;
    const currentUser = oidc?.user;
    const headerRowId = showPopup.headerRowId;
    const recordNumber = showPopup.recordNumber;

    const gridRef = useRef(null);
    const gridKey = "DEE19A30-F9FE-4A19-BD36-DD8A895EA19E";
    const statusFormRefs = useRef({});
    /* Delphi opened with a current record, so the panels below were never blank. One automatic
       selection per load reproduces that without fighting the user's own clicks. */
    const autoSelectedRef = useRef(false);

    const [showAll, setShowAll] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_PRIMARY);
    const [statusFields, setStatusFields] = useState({});
    const [check, setCheck] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [itemQuantities, setItemQuantities] = useState(null);
    const [lotQuantities, setLotQuantities] = useState(null);
    const [assignedLocations, setAssignedLocations] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [busy, setBusy] = useState(false);
    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${accessToken}`, access_token: accessToken }),
        [accessToken]
    );
    const [currentLayout, setCurrentLayout] = useState("Default");
    const loadState = useCallback(async () => {
        const state = await GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout);
        /* Selection and focus are part of a DataGrid's state, and GridLayout.sendStorageRequest blanks
           selectedRowKeys before it saves. Applying a loaded state therefore reaches in and clears
           whichever row the user picked - which reads as the grid selecting and re-selecting rows on
           its own. A restored layout should bring back columns, widths and sorting, never selection. */
        if (state) {
            delete state.selectedRowKeys;
            delete state.focusedRowKey;
            delete state.focusedRowIndex;
        }
        return state;
    }, [gridKey, accessToken, currentLayout]);

    const saveState = useCallback((state) => { GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout); }, [gridKey, accessToken, currentLayout]);

    /* Delphi FormCreate resets fShowInsufficient on create only; the web resets it on every open. */
    useEffect(() => {
        if (!visible) return;
        setShowAll(false);
        setActiveTab(TAB_PRIMARY);
        setCheck(showPopup.check ?? null);
        setSelectedRow(null);
        setItemQuantities(null);
        setLotQuantities(null);
        setAssignedLocations([]);
        setAvailableLocations([]);
        autoSelectedRef.current = false;
    }, [visible, showPopup.check]);

    const dataSource = useMemo(() => {
        if (!visible || !headerRowId || !accessToken) return null;
        return createStore({
            key: "RowId",
            loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetItemsWithUnavailableQuantities}`,
            loadParams: {
                headerRowId,
                stageFrom: showPopup.stageFrom,
                stageTo: showPopup.stageTo,
                /* The gate itself depends on the Ship Quantity Basis answer (uOMM0000.pas:4142), so the
                   insufficient-only list has to be asked for with the same answer the check used. */
                shipQuantityBasis: showPopup.shipQuantityBasis ?? "",
                showAll,
            },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = authHeaders;
            },
        });
    }, [
        visible,
        headerRowId,
        showAll,
        accessToken,
        authHeaders,
        showPopup.stageFrom,
        showPopup.stageTo,
        showPopup.shipQuantityBasis,
    ]);


    /* A new store means a new row set, so the automatic first-row selection applies again. Kept out of
       the useMemo above so building the store stays a pure render. */
    useEffect(() => {
        autoSelectedRef.current = false;
    }, [dataSource]);

    const loadItemStatus = useCallback(
        async (row) => {
            if (!row?.RItem || row.RItem === EMPTY_GUID) {
                setItemQuantities(null);
                setLotQuantities(null);
                return;
            }
            const url = `${window._env_.REACT_APP_APIS}/${routes.OM.GetImItemStatusQuantitySummary}`;
            const hasLot = row.RLot && row.RLot !== EMPTY_GUID;
            try {
                /* Decision 5: the existing endpoint is called twice - item side, then lot side. */
                const [itemResponse, lotResponse] = await Promise.all([
                    axios.get(url, { params: { rItem: row.RItem, rLot: EMPTY_GUID }, headers: authHeaders }),
                    hasLot
                        ? axios.get(url, { params: { rItem: row.RItem, rLot: row.RLot }, headers: authHeaders })
                        : Promise.resolve(null),
                ]);
                setItemQuantities(itemResponse?.data || {});
                setLotQuantities(hasLot ? lotResponse?.data || {} : null);
            } catch (error) {
                setItemQuantities(null);
                setLotQuantities(null);
            }
        },
        [authHeaders]
    );

    const loadLocations = useCallback(
        async (row) => {
            if (!row?.RowId) {
                setAssignedLocations([]);
                setAvailableLocations([]);
                return;
            }
            try {
                const [assigned, available] = await Promise.all([
                    axios.get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetDetailAssignedLocations}`, {
                        params: { rowId: row.RowId },
                        headers: authHeaders,
                    }),
                    axios.get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetDetailAvailableLocations}`, {
                        params: { rowId: row.RowId },
                        headers: authHeaders,
                    }),
                ]);
                setAssignedLocations(assigned?.data || []);
                setAvailableLocations(available?.data || []);
            } catch (error) {
                setAssignedLocations([]);
                setAvailableLocations([]);
            }
        },
        [authHeaders]
    );

    /* Delphi qryOMTransactionDetailAfterScroll: both panels follow the selected line. */
    const onSelectionChanged = useCallback(
        ({ selectedRowsData }) => {
            const row = selectedRowsData && selectedRowsData.length === 1 ? selectedRowsData[0] : null;
            /* The Delphi grid always had a current record, so an empty selection is not a state this
               dialog shows. It also arrives spuriously when StateStoring re-applies grid state, and
               acting on it would blank the panels and re-fetch on every save/load round trip. */
            if (!row) return;
            setSelectedRow(row);
            loadItemStatus(row);
            loadLocations(row);
        },
        [loadItemStatus, loadLocations]
    );

    /* The Delphi grid always had a current record when the dialog opened, so its bottom panels were
       never blank. Select the first row once per load to match that - the guard keeps the grid's own
       repaints (paging, layout restore, column chooser) from stealing the user's selection back. */
    const onContentReady = useCallback((e) => {
        if (autoSelectedRef.current) return;
        if (e.component.totalCount() <= 0) return;
        if ((e.component.getSelectedRowKeys() || []).length > 0) {
            autoSelectedRef.current = true;
            return;
        }
        autoSelectedRef.current = true;
        e.component.selectRowsByIndexes([0]);
    }, []);

    /* Delphi grdItemsCustomDraw: bold + warning colour on the short lines. */
    const onCellPrepared = useCallback((e) => {
        if (e.rowType !== "data" || !isRowInsufficient(e.data)) return;
        e.cellElement.style.fontWeight = "bold";
        e.cellElement.style.color = window._env_.NegativeValueColor;
    }, []);

    const onAssignedCellPrepared = useCallback((e) => {
        if (e.rowType !== "data") return;
        if (toNumber(e.data?.Quantity) <= toNumber(e.data?.AvailableQuantity)) return;
        e.cellElement.style.fontWeight = "bold";
        e.cellElement.style.color = window._env_.NegativeValueColor;
    }, []);

    const close = useCallback(() => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const handleCancel = useCallback(() => {
        close();
        if (onCancel) onCancel();
    }, [close, onCancel]);

    /**
     * Delphi: Continue runs VerifyAvailabilityChanges and, when availability moved, shows the message
     * and recurses into DisplayErrors. Here that is the verify endpoint followed by a fresh check.
     */
    const handleContinue = useCallback(async () => {
        setBusy(true);
        try {
            const verify = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.VerifyItemAvailabilityChanges}`,
                {
                    HeaderRowId: headerRowId,
                    StageFrom: showPopup.stageFrom,
                    StageTo: showPopup.stageTo,
                    ShipQuantityBasis: showPopup.shipQuantityBasis ?? null,
                    Availability: check?.Availability ?? [],
                },
                { headers: authHeaders }
            );

            if (!verify?.data?.AvailabilityChanged) {
                close();
                if (onContinue) await onContinue();
                return;
            }

            await alert(verify.data.Message, MESSAGES.INFORMATION);

            const recheck = await axios.get(
                `${window._env_.REACT_APP_APIS}/${routes.OM.CheckItemsWithUnavailableQuantities}`,
                {
                    params: {
                        headerRowId,
                        stageFrom: showPopup.stageFrom,
                        stageTo: showPopup.stageTo,
                        shipQuantityBasis: showPopup.shipQuantityBasis ?? "",
                    },
                    headers: authHeaders,
                }
            );

            if (!recheck?.data?.HasUnavailableQuantities) {
                close();
                if (onContinue) await onContinue();
                return;
            }

            setCheck(recheck.data);
            setShowAll(false);
            /* A fresh check means a fresh row set, so the automatic first-row selection applies again. */
            autoSelectedRef.current = false;
            gridRef.current.instance().refresh();
        } catch (error) {
            await alert(
                error?.response?.data?.message || error?.message || MESSAGES.STAGE_CHANGE_FAILED,
                MESSAGES.ERROR
            );
        } finally {
            setBusy(false);
        }
    }, [
        authHeaders,
        check,
        close,
        headerRowId,
        onContinue,
        showPopup.stageFrom,
        showPopup.stageTo,
        showPopup.shipQuantityBasis,
    ]);


    /* Labels and the Unavailable = In + Out definition come from the shared IM Item Status layout.
       Only the Quantity Status group is shown, and only the ten rows OMD0016 itself displayed. */
    const statusSchema = useMemo(() => {
        const group = getImItemStatusLayout().find((g) => g.groupId === QUANTITY_STATUS_GROUP_ID);
        if (!group) return [];
        const fields = group.fields.filter(
            (field) => !STATUS_FIELDS_NOT_ON_OMD0016.includes(field.name)
        );
        return [
            { ...group, label: MESSAGES.UNAVAILABLE_QUANTITIES_ITEM_STATUS, fields },
            {
                ...group,
                groupId: LOT_STATUS_GROUP_ID,
                label: MESSAGES.UNAVAILABLE_QUANTITIES_LOT_STATUS,
                fields: fields.map((field) => ({ ...field, name: field.name + LOT_FIELD_SUFFIX })),
            },
        ];
    }, []);

    useEffect(() => {
        const item = buildImItemStatusFormFields({}, itemQuantities, false);
        const lot = buildImItemStatusFormFields({}, lotQuantities, false);
        const merged = { ...item };
        Object.keys(lot).forEach((key) => {
            merged[key + LOT_FIELD_SUFFIX] = lot[key];
        });
        setStatusFields(merged);
    }, [itemQuantities, lotQuantities]);

    /* The reports this dialog offers, i.e. the Delphi form's TAbReportObjects with ShowOnMenu = True.
       One entry today; the menu is the shape the legacy Print popup had, so a second one is just
       another row here. */
    const printMenuItems = useMemo(
        () => [
            {
                text: MESSAGES.UNAVAILABLE_QUANTITIES_REPORT,
                permission: INSUFFICIENT_QUANTITY_REPORT.permission,
                report: {
                    name: INSUFFICIENT_QUANTITY_REPORT.name,
                    title: MESSAGES.UNAVAILABLE_QUANTITIES_REPORT,
                },
            },
        ],
        []
    );

    if (!visible) return null;

    const allowContinue = check?.AllowContinue === true;

    return (
        <Popup
            visible={true}
            onHiding={handleCancel}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={MESSAGES.UNAVAILABLE_QUANTITIES_TITLE}
            container="#root"
            width="92vw"
            height="92vh"
        >
            <div className="d-flex flex-column" style={{ height: "100%" }}>
                {/* Delphi btnShowInsufficient: one button whose caption flips with the state. */}
                <div className="d-flex mb-2">
                    <Button
                        text={
                            showAll
                                ? MESSAGES.UNAVAILABLE_QUANTITIES_SHOW_INSUFFICIENT
                                : MESSAGES.UNAVAILABLE_QUANTITIES_SHOW_ALL
                        }
                        type="default"
                        disabled={busy}
                        onClick={() => setShowAll((previous) => !previous)}
                    />
                    {/* Delphi navTop Print button -> a popup listing every TAbReportObject on the form
                        with ShowOnMenu = True, which here is just roOMInsufficientQuantity. The shared
                        PrintReportMenu is that button: it applies the OMReports gate, scopes the report
                        to this order's Record # and hosts it in the report popup, so the dialog only
                        has to say which reports it offers. `embedded` is what makes the criteria grid
                        and the Available Fields list size themselves from the popup rather than the
                        window. Deliberately not modelled on the opInsufficientQuantityReport menu item
                        - that has no OnClick in the DFM and does nothing in Delphi. */}
                    <PrintReportMenu
                        className="ml-2"
                        items={printMenuItems}
                        embedded={true}
                        selectedRecord={{ recordNumber }}
                        disabled={busy || !recordNumber}
                    />
                </div>

                {/* Delphi lblInsufficient - red banner above grdItems. */}
                <div className="mb-2" style={{ textAlign: "center", fontWeight: "bold", color: window._env_.NegativeValueColor }}>
                    {MESSAGES.UNAVAILABLE_QUANTITIES_BANNER}
                </div>

                <div style={{ flex: "1 1 auto", minHeight: "220px" }}>
                    <DataGrid
                        ref={gridRef}
                        dataSource={dataSource}
                        keyExpr="RowId"
                        height="100%"
                        showBorders={true}
                        hoverStateEnabled={true}
                        columnAutoWidth={true}
                        allowColumnResizing={true}
                        remoteOperations={true}
                        onSelectionChanged={onSelectionChanged}
                        onContentReady={onContentReady}
                        onCellPrepared={onCellPrepared}
                        onExporting={(e) => {
                            if (window.handleDevExtremeDataGridExport) {
                                window.handleDevExtremeDataGridExport(e);
                            }
                        }}
                    >
                        {/* Pairs with the exportButton toolbar item below - without this the button
                            never renders. */}
                        <Export enabled={true} allowExportSelectedData={false} />
                        <Selection mode="single" />
                        <Scrolling mode="standard" />
                        <Paging defaultPageSize={DEFAULT_PAGE_SIZE} />
                        <Pager
                            visible={true}
                            showInfo={true}
                            showPageSizeSelector={true}
                            allowedPageSizes={PAGE_SIZES}
                            showNavigationButtons={true}
                        />
                        <ColumnChooser
                            enabled={true}
                            title="Choose Column"
                            width={290}
                            height={290}
                            sortOrder="asc"
                        >
                            <ColumnChooserSearch
                                enabled={true}
                                editorOptions={{ placeholder: 'Search column' }} />
                        </ColumnChooser>
                        <StateStoring
                            enabled={true}
                            type="custom"
                            customLoad={loadState}
                            storageKey={gridKey}
                            customSave={saveState}
                        />
                        {/* Keys and the two fields the row highlighting reads - plumbing, not for the chooser. */}
                        <Column dataField="RowId" visible={false} showInColumnChooser={false} />
                        <Column dataField="RItem" visible={false} showInColumnChooser={false} />
                        <Column dataField="RLot" visible={false} showInColumnChooser={false} />

                        {/* The rest of the Delphi FieldList: selected by the legacy query, not shown by grdItems.
                            Hidden here too, but available through the column chooser. */}
                        <Column
                            dataField="DetailType"
                            caption={COLUMN.DETAIL_TYPE}
                            visible={false}
                        />
                        <Column
                            dataField="ReferenceNumber"
                            caption={COLUMN.REFERENCE_NUMBER}
                            visible={false}
                        />
                        <Column
                            dataField="Stage"
                            caption={COLUMN.STAGE}
                            visible={false}
                        />
                        <Column
                            dataField="ItemType"
                            caption={COLUMN.ITEM_TYPE}
                            visible={false}
                        />
                        <Column
                            dataField="ItemDescription2"
                            caption={COLUMN.ITEM_DESCRIPTION_2}
                            visible={false}
                        />
                        <Column
                            dataField="LotDescription1"
                            caption={COLUMN.LOT_DESCRIPTION_1}
                            visible={false}
                        />
                        <Column
                            dataField="LotDescription2"
                            caption={COLUMN.LOT_DESCRIPTION_2}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedCode1"
                            caption={COLUMN.USER_DEFINED_CODE_1}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedCode2"
                            caption={COLUMN.USER_DEFINED_CODE_2}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedCode3"
                            caption={COLUMN.USER_DEFINED_CODE_3}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedCode4"
                            caption={COLUMN.USER_DEFINED_CODE_4}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedCode5"
                            caption={COLUMN.USER_DEFINED_CODE_5}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedDate1"
                            caption={COLUMN.USER_DEFINED_DATE_1}
                            dataType="date"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedDate2"
                            caption={COLUMN.USER_DEFINED_DATE_2}
                            dataType="date"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedDate3"
                            caption={COLUMN.USER_DEFINED_DATE_3}
                            dataType="date"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedDate4"
                            caption={COLUMN.USER_DEFINED_DATE_4}
                            dataType="date"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedDate5"
                            caption={COLUMN.USER_DEFINED_DATE_5}
                            dataType="date"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedQuantity1"
                            caption={COLUMN.USER_DEFINED_QUANTITY_1}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedQuantity2"
                            caption={COLUMN.USER_DEFINED_QUANTITY_2}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedQuantity3"
                            caption={COLUMN.USER_DEFINED_QUANTITY_3}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedQuantity4"
                            caption={COLUMN.USER_DEFINED_QUANTITY_4}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedQuantity5"
                            caption={COLUMN.USER_DEFINED_QUANTITY_5}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedReference1"
                            caption={COLUMN.USER_DEFINED_REFERENCE_1}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedReference2"
                            caption={COLUMN.USER_DEFINED_REFERENCE_2}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedReference3"
                            caption={COLUMN.USER_DEFINED_REFERENCE_3}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedReference4"
                            caption={COLUMN.USER_DEFINED_REFERENCE_4}
                            visible={false}
                        />
                        <Column
                            dataField="UserDefinedReference5"
                            caption={COLUMN.USER_DEFINED_REFERENCE_5}
                            visible={false}
                        />
                        <Column
                            dataField="OnHandItem"
                            caption={COLUMN.ITEM_ON_HAND}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="InReceivingItem"
                            caption={COLUMN.ITEM_IN_RECEIVING}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="OutShippedItem"
                            caption={COLUMN.ITEM_IN_SHIPPING}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="OutUnavailableItem"
                            caption={COLUMN.ITEM_UNAVAILABLE}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="AvailableItem"
                            caption={COLUMN.ITEM_AVAILABLE_STATUS}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="InOrderedItem"
                            caption={COLUMN.ITEM_PENDING_REC}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="OutOrderedItem"
                            caption={COLUMN.ITEM_PENDING_SHIP}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="NetAvailableItem"
                            caption={COLUMN.ITEM_NET_AVAILABLE}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="FutureRecItem"
                            caption={COLUMN.ITEM_FUTURE_REC}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="FutureShipItem"
                            caption={COLUMN.ITEM_FUTURE_SHIP}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="OnHandLot"
                            caption={COLUMN.LOT_ON_HAND}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="InReceivingLot"
                            caption={COLUMN.LOT_IN_RECEIVING}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="OutShippedLot"
                            caption={COLUMN.LOT_IN_SHIPPING}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="OutUnavailableLot"
                            caption={COLUMN.LOT_UNAVAILABLE}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="AvailableLot"
                            caption={COLUMN.LOT_AVAILABLE_STATUS}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="InOrderedLot"
                            caption={COLUMN.LOT_PENDING_REC}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="OutOrderedLot"
                            caption={COLUMN.LOT_PENDING_SHIP}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="NetAvailableLot"
                            caption={COLUMN.LOT_NET_AVAILABLE}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="FutureRecLot"
                            caption={COLUMN.LOT_FUTURE_REC}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />
                        <Column
                            dataField="FutureShipLot"
                            caption={COLUMN.LOT_FUTURE_SHIP}
                            dataType="number"
                            format="#,##0.00"
                            visible={false}
                        />

                        {/* The nine columns grdItems actually displayed. */}
                        <Column
                            dataField="RecordNumber"
                            caption={COLUMN.RECORD_NUMBER}
                            allowEditing={false}
                        />
                        <Column dataField="WarehouseId" caption={COLUMN.WAREHOUSE_SHORT} />
                        <Column dataField="ItemCode" caption={COLUMN.ITEM} />
                        <Column
                            dataField="ItemDescription1"
                            caption={COLUMN.ITEM_DESCRIPTION_1}
                        />
                        <Column dataField="LotCode" caption={COLUMN.LOT} />
                        <Column
                            dataField="QuantityOrdered"
                            caption={COLUMN.ORDERED}
                            dataType="number"
                            format="#,##0.00"
                            customizeText={customizeOrderQuantity}
                        />
                        <Column
                            dataField="QuantityShipped"
                            caption={COLUMN.SHIPPED}
                            dataType="number"
                            format="#,##0.00"
                            customizeText={customizeOrderQuantity}
                        />
                        <Column
                            dataField="QuantityBackOrder"
                            caption={COLUMN.BACKORDER}
                            dataType="number"
                            format="#,##0.00"
                            customizeText={customizeOrderQuantity}
                        />
                        <Column
                            dataField="ItemAvailable"
                            caption={COLUMN.ITEM_AVAILABLE}
                            dataType="number"
                            format="#,##0.00"
                        />
                        <Column
                            dataField="LotAvailable"
                            caption={COLUMN.LOT_AVAILABLE}
                            dataType="number"
                            format="#,##0.00"
                        />
                        {/* Toolbar items are DevExtreme templates: devextreme-react tears them down and
                            rebuilds them on every host re-render, remounting GridLayoutManager and
                            re-running its GetGridLayouts fetch each time. Nothing driven by popup state
                            belongs here - that is why Show All and Continue sit outside the grid. */}
                        <Toolbar location="after">
                            <Item location="after">
                                <GridLayoutManager gridKey={gridKey} dataGrid={gridRef} setCurrentLayout={setCurrentLayout} />
                            </Item>
                            <Item name="exportButton" />
                            <Item name="columnChooserButton" />
                        </Toolbar>
                    </DataGrid>
                </div>

                {/* Only reachable when the grid came back empty - the first row is selected
                    automatically otherwise, the way the Delphi dialog always had a current record. */}
                {!selectedRow && (
                    <div className="mb-2 text-muted" style={{ textAlign: "center" }}>
                        {MESSAGES.UNAVAILABLE_QUANTITIES_SELECT_ROW}
                    </div>
                )}

                {/* Delphi pgcBottom: Primary carries the two location grids, Status the
                    item and lot quantity panels. */}
                <div className="unavailable-quantities-tabs" style={{ flex: "0 0 auto", height: "360px" }}>
                    <TabPanel
                        selectedIndex={activeTab}
                        onSelectionChanged={(e) => {
                            const index = e.component.option("selectedIndex");
                            if (typeof index === "number") setActiveTab(index);
                        }}
                        height="100%"
                        animationEnabled={false}
                        swipeEnabled={false}
                        deferRendering={false}
                    >
                        <TabItem title={MESSAGES.UNAVAILABLE_QUANTITIES_TAB_PRIMARY}>
                            <div className="row">
                                <div className="col-6">
                                    <fieldset className="col-fieldset">
                                        <legend>
                                            {MESSAGES.UNAVAILABLE_QUANTITIES_ASSIGNED_LOCATIONS}
                                        </legend>
                                        {/* Record # is IMTRANSACTIONLOCATION.KEYSEQUENCE. Delphi
                                            qryTransLoc surfaced OMTRANSACTIONDETAIL.KEYSEQUENCE, so
                                            every row showed the same number; this identifies the row
                                            the user is actually looking at. Available Locations gets
                                            one too, which legacy had no column for at all. */}
                                        <DataGrid
                                            dataSource={assignedLocations}
                                            height={220}
                                            showBorders={true}
                                            columnAutoWidth={true}
                                            onCellPrepared={onAssignedCellPrepared}
                                        >
                                            <Scrolling mode="standard" />
                                            <Paging enabled={false} />
                                            <Column
                                                dataField="RecordNumber"
                                                caption={COLUMN.RECORD_NUMBER}
                                                allowEditing={false}
                                            />
                                            <Column dataField="WarehouseId" caption={COLUMN.WAREHOUSE_SHORT} />
                                            <Column dataField="LocationCode" caption={COLUMN.LOCATION} />
                                            <Column
                                                dataField="Quantity"
                                                caption={COLUMN.QUANTITY}
                                                dataType="number"
                                                format="#,##0.00"
                                            />
                                            <Column
                                                dataField="AvailableQuantity"
                                                caption={COLUMN.AVAILABLE}
                                                dataType="number"
                                                format="#,##0.00"
                                            />
                                            <Column
                                                dataField="ValidateQuantity"
                                                caption={COLUMN.VALIDATE_QUANTITY}
                                            />
                                        </DataGrid>
                                    </fieldset>
                                </div>
                                <div className="col-6">
                                    <fieldset className="col-fieldset">
                                        <legend>
                                            {MESSAGES.UNAVAILABLE_QUANTITIES_AVAILABLE_LOCATIONS}
                                        </legend>
                                        <DataGrid
                                            dataSource={availableLocations}
                                            height={220}
                                            showBorders={true}
                                            columnAutoWidth={true}
                                        >
                                            <Scrolling mode="standard" />
                                            <Paging enabled={false} />
                                            <Column
                                                dataField="RecordNumber"
                                                caption={COLUMN.RECORD_NUMBER}
                                                allowEditing={false}
                                            />
                                            <Column dataField="WarehouseId" caption={COLUMN.WAREHOUSE_SHORT} />
                                            <Column dataField="LocationCode" caption={COLUMN.LOCATION} />
                                            <Column
                                                dataField="AvailableQuantity"
                                                caption={COLUMN.AVAILABLE}
                                                dataType="number"
                                                format="#,##0.00"
                                            />
                                        </DataGrid>
                                    </fieldset>
                                </div>
                            </div>
                        </TabItem>

                        <TabItem title={MESSAGES.UNAVAILABLE_QUANTITIES_TAB_STATUS}>
                            <div className="unavailable-quantities-status">
                                <FormField
                                    currrentUser={currentUser}
                                    layoutId={STATUS_LAYOUT_ID}
                                    layoutName={STATUS_LAYOUT_NAME}
                                    defaultFormSchema={statusSchema}
                                    formFields={statusFields}
                                    setformFields={setStatusFields}
                                    handleFieldValueChange={noop}
                                    DYNAMICFORMSETTING="No"
                                    formRefs={statusFormRefs}
                                    onFormLoad={noop}
                                />
                            </div>
                        </TabItem>
                    </TabPanel>
                </div>

                <div className="d-flex justify-content-end pt-2">
                    {/* Delphi btnContinue.Visible := not (flag in ['B','D']) - a hard block shows
                        Cancel only, so Cancel is the one control that must always be present. */}
                    {allowContinue && (
                        <Button
                            text={MESSAGES.UNAVAILABLE_QUANTITIES_CONTINUE}
                            type="default"
                            width={140}
                            disabled={busy}
                            onClick={handleContinue}
                            className="mr-2"
                        />
                    )}
                    <Button
                        text={MESSAGES.UNAVAILABLE_QUANTITIES_CANCEL}
                        width={140}
                        disabled={busy}
                        onClick={handleCancel}
                    />
                </div>
            </div>
        </Popup>
    );
}


function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(ItemsWithUnavailableQuantitiesPopup);
